import React, { useCallback, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { locales, languageMap, defaultLanguage, messages } from '../common/language';
import axios from 'axios';
import { apiUrl } from '../common/constants';
import { headersAPIObjectPublic } from '../common/utils';
import '../common/styles.css';

export default function PublicOccupancy() {
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const { friendly_url: friendlyUrl = '' } = useParams();
    const classroomId = query.get('classrooms_id');
    const colorParameter = query.get('color');
    const color = colorParameter && /^#?[0-9a-f]{6}$/i.test(colorParameter)
        ? `#${colorParameter.replace('#', '')}`
        : '#4054b2';
    const valid = Boolean(friendlyUrl) && (classroomId === null || /^[1-9]\d*$/.test(classroomId));
    const [error, setError] = useState(false);
    const [association, setAssociation] = useState(null);
    const language = association && association.friendlyUrl === friendlyUrl ? association.language : languageMap[defaultLanguage];
    const texts = messages[language].public_occupancy;
    const calendar = useRef(null);
    const requestVersion = useRef(0);
    const loadEvents = useCallback((info, success, failure) => {
        const version = ++requestVersion.current;
        setError(false);
        // FullCalendar uses UTC coercion to retain Europe/Madrid wall-clock times.
        const localDate = value => value.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '');
        const loadAllPages = async () => {
            const { data: entity } = await axios.get(`${apiUrl}/associations/public/str/${encodeURIComponent(friendlyUrl)}`, {
                headers: headersAPIObjectPublic()
            });
            if (version !== requestVersion.current) return [];
            const entityLanguage = languageMap[entity.lang] || languageMap[defaultLanguage];
            setAssociation({ friendlyUrl, language: entityLanguage });
            const slots = [];
            const start = localDate(info.startStr);
            const end = localDate(info.endStr);
            let page = 0;
            let last = false;
            while (!last && version === requestVersion.current) {
                const { data } = await axios.get(`${apiUrl}/events/public/sessions`, {
                    headers: headersAPIObjectPublic(),
                    params: { friendly_url: friendlyUrl, classrooms_id: classroomId === null ? undefined : classroomId,
                        start_gte: start, start_lte: end, page, size: 100 }
                });
                data.content.forEach(event => (event.sessions || []).forEach(session => {
                    const slotStart = localDate(session.start || '');
                    const slotEnd = localDate(session.end || session.start || '');
                    if (!session.cancelled && slotStart && slotStart < end && slotEnd >= start) {
                        slots.push({ start: slotStart, end: slotEnd, title: messages[entityLanguage].public_occupancy.occupied, allDay: false });
                    }
                }));
                last = data.last;
                page += 1;
            }
            return slots;
        };
        loadAllPages().then(slots => {
            if (version === requestVersion.current) {
                success(slots);
            } else { success([]); }
        }).catch(cause => {
            if (version === requestVersion.current) { setError({ notFound: cause.response && cause.response.status === 404, message: cause.response && cause.response.data && cause.response.data.message }); }
            failure(cause);
        });
    }, [friendlyUrl, classroomId]);

    if (!valid) return <main className="public-occupancy">
        <p role="alert">{texts.invalid_parameters}</p></main>;
    return <main className="public-occupancy" style={{ '--public-occupancy-color': color }}>
        <div className="public-occupancy-status" aria-live="polite">
            {error ? <span role="alert">{error.message || texts.load_error}
                {!error.notFound && <>{' '}<button onClick={() => calendar.current.getApi().refetchEvents()}>{texts.retry}</button></>}
            </span> : null}
        </div>
        <div hidden={(error && error.notFound) || !association || association.friendlyUrl !== friendlyUrl}>
        <FullCalendar key={`${friendlyUrl}-${classroomId}`} ref={calendar}
            plugins={[dayGridPlugin, timeGridPlugin]} locale={locales[language]} timeZone="Europe/Madrid"
            initialView="dayGridMonth" firstDay={1} height="auto" events={loadEvents}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
            editable={false} selectable={false} displayEventEnd eventDisplay="block" dayMaxEvents={false}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            allDaySlot={false} slotDuration="00:30:00" nowIndicator
        />
        </div>
    </main>;
}
